<template>
    <OnBoardingForm :title="$t('Assets - On Boarding')" :current-step="3">

        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">

            <form class="flex flex-col space-y-6" @submit.prevent="submitForm">
                <div>
                    <div class="sm:hidden">
                        <label for="tabs" class="sr-only">{{ $t('Select a person') }}</label>
                        <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                        <select id="tabs" name="tabs"
                                class="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500">
                            <option v-for="(tab, tabIdx) in accountOwners" :key="tabIdx"
                                    :selected="isActivePerson(tabIdx)">
                                {{ $t('Contracting party :number: ', { number: tabIdx + 1 }) }}{{ nameFilled(tab) ? `${tab.firstname} ${tab.lastname}` : '' }}
                            </option>
                        </select>
                    </div>
                    <div class="hidden sm:block">
                        <nav class="isolate flex divide-x divide-gray-200 rounded-lg shadow" :aria-label="$t('Tabs')">
                            <a v-for="(tab, tabIdx) in accountOwners" :key="tabIdx" href="#"
                               @click.prevent="setActivePerson(tabIdx)"
                               :class="[isActivePerson(tabIdx) ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700', tabIdx === 0 ? 'rounded-l-lg' : '', tabIdx === accountOwners.length - 1 ? 'rounded-r-lg' : '', 'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium hover:bg-gray-50 focus:z-10', hasAnyErrors('asset_information.incomes',tabIdx) ? 'bg-red-100 hover:bg-red-50':'']"
                               :aria-current="isActivePerson(tabIdx) ? 'page' : undefined">
                               <span class="flex items-center">
                                         <span class="flex items-center pr-2">
                                             <span class="pr-2"
                                                   v-if="hasAnyErrors('asset_information.incomes',tabIdx)">
                                                 <ExclamationCircleIcon
                                                     class="h-5 w-5" aria-hidden="true"/>
                                             </span>
                                             {{ $t('Contracting party :number: ', { number: tabIdx + 1 }) }}
                                         </span>
                                         <span class="font-bold" v-if="nameFilled(tab)">{{ tab.firstname }} {{ tab.lastname }}</span>
                                </span>
                                <span aria-hidden="true"
                                      :class="[isActivePerson(tabIdx) ? 'bg-primary-500' : 'bg-transparent', 'absolute inset-x-0 bottom-0 h-0.5']"/>
                            </a>
                        </nav>
                    </div>
                </div>

                <div class="flex flex-col space-y-6 px-6">
                    <template v-for="(person, personIdx) in form.asset_information.incomes">
                        <template v-if="isActivePerson(personIdx)">
                            <FormSection>
                                <template #title>
                                    {{ $t('Information about the profession') }}
                                </template>
                                <template #description>

                                </template>

                                <div class="grid gap-6 grid-cols-12">

                                    <div class="col-span-12">
                                        <Label :value="$t('Profession')" class="mb-3"/>
                                        <SimpleRadioGroupOptions :options="professionOptions"
                                                                 containerClasses="flex flex-wrap space-x-3"
                                                                 v-model="person.profession_type"></SimpleRadioGroupOptions>
                                        <JetInputError class="mt-3"
                                                       :message="form.errors[`asset_information.incomes.${personIdx}.profession_type`]"></JetInputError>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="job_information"
                                               :value="$t('Position, employer or company information for self-employment and information about the last job for retirement.')"/>
                                        <JetTextArea
                                            id="job_information"
                                            v-model="person.job_information"
                                            class="mt-1 block w-full"
                                            autocomplete="job_information"
                                        />
                                        <JetInputError :message="form.errors[`asset_information.incomes.${personIdx}.job_information`]" class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="industry" :value="$t('Industry, sector')"/>
                                        <JetInput
                                            id="industry"
                                            v-model="person.industry"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="industry"
                                        />
                                        <JetInputError :message="form.errors[`asset_information.incomes.${personIdx}.industry`]" class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="net_income" :value="$t('Estimated annual net income')"/>
                                        <JetNumberInput
                                            id="net_income"
                                            v-model="person.net_income"
                                            class="mt-1 block w-full"
                                            autocomplete="income"
                                        />
                                        <JetInputError :message="form.errors[`asset_information.incomes.${personIdx}.net_income`]" class="mt-2"/>
                                    </div>


                                </div>
                            </FormSection>
                        </template>
                    </template>
                </div>


                <JetSectionBorder />

                <FormSection>
                    <template #title>
                        {{ $t('Information about the assets') }}
                    </template>
                    <template #description>

                    </template>

                    <div class="grid gap-6 grid-cols-12">

                        <div class="col-span-12">
                            <Label for="total_assets_estimate" :value="$t('Estimated total assets')"/>
                            <JetNumberInput
                                id="total_assets_estimate"
                                v-model="form.asset_information.total_assets_estimate"
                                class="mt-1 block w-full"
                                autocomplete="total_assets_estimate"
                            />
                            <JetInputError :message="form.errors[`asset_information.total_assets_estimate`]" class="mt-2"/>
                        </div>

                        <div class="col-span-12">
                            <Label for="expected_assets_income" :value="$t('Amount of incoming assets')"/>
                            <JetNumberInput
                                id="expected_assets_income"
                                v-model="form.asset_information.expected_assets_income"
                                class="mt-1 block w-full"
                                autocomplete="expected_assets_income"
                            />
                            <JetInputError :message="form.errors[`asset_information.expected_assets_income`]" class="mt-2"/>
                        </div>

                        <div class="col-span-12">
                            <Label :value="$t('Origin of assets (multiple possible)')" class="mb-3"/>
                            <SimpleCheckboxList
                                :options="assetsSource"
                                v-model="form.asset_information.assets_source"
                            ></SimpleCheckboxList>
                            <JetInputError class="mt-3"
                                           :message="form.errors[`asset_information.assets_source`]"></JetInputError>
                        </div>

                    </div>
                </FormSection>


                <div class="flex justify-end">
                    <JetActionMessage :on="form.recentlySuccessful" class="mr-3">
                        {{ $t('Saved.') }}
                    </JetActionMessage>

                    <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ $t('Save & continue') }}
                    </JetButton>
                </div>
            </form>
        </div>

    </OnBoardingForm>
</template>

<script>
import OnBoardingForm from '@/Layouts/OnBoardingForm.vue';
import FormSection from "@/Components/Form/FormSection";
import Label from '@/Components/Form/Label';
import JetButton from "@/Jetstream/Button";
import JetActionMessage from "@/Jetstream/ActionMessage";
import JetInput from '@/Jetstream/Input.vue';
import JetNumberInput from '@/Jetstream/NumberInput.vue';
import JetTextArea from '@/Jetstream/Textarea.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetSectionBorder from '@/Jetstream/SectionBorder.vue';
import {defineComponent} from "vue";
import SimpleRadioGroupOptions from "../../Components/Form/SimpleRadioGroupOptions";
import PersonTabsMixin from "../../Mixins/PersonTabsMixin";
import SimpleCheckboxList from "../../Components/Form/SimpleCheckboxList";
import {ExclamationCircleIcon} from "@heroicons/vue/outline";

export default defineComponent({
    components: {
        ExclamationCircleIcon,
        SimpleCheckboxList,
        OnBoardingForm,
        SimpleRadioGroupOptions,
        FormSection,
        JetInputError,
        Label,
        JetButton,
        JetActionMessage,
        JetInput,
        JetTextArea,
        JetSectionBorder,
        JetNumberInput,
    },
    mixins: [
        PersonTabsMixin,
    ],
    computed: {},
    props: {
        accountOwners: Array,
        assetInfo: Object,
        errors: Object,
    },
    data() {
        return {
            form: this.$inertia.form({
                asset_information: this.initializeAssetInformation(),
            }),
            professionOptions: [
                {type: 'non_independent', name: 'Employed'},
                {type: 'independent', name: 'Self-employed'},
                {type: 'retired', name: 'Retired'},
            ],
            assetsSource: [
                {type: 'acquisition_or_business_activities', name: 'Employment or business activity'},
                {type: 'inheritance', name: 'Inheritance'},
                {type: 'gift', name: 'Gifts'},
                {type: 'real_estate_sales', name: 'Real estate sale'},
                {type: 'real_estate_yields', name: 'Real estate income'},
                {type: 'accumulation_of_savings', name: 'Accumulation of savings'},
                {type: 'trading', name: 'Trading'},
                {type: 'others', name: 'Other'},
            ]
        };
    },
    methods: {
        submitForm() {
            this.form.post(route('onboarding.step3.store'), {
                preserveScroll: false,
                preserveState: true,
                onError: () => {
                    this.handleFormError();
                }
            })
        },

        handleFormError(){
            if (!this.hasAnyErrors('asset_information.incomes', this.activePerson))
                this.setActivePerson(this.activePerson == 0 ? 1 : 0);
        },

        initializeAssetInformation() {
            let baseInformation = {
                incomes: [],
                total_assets_estimate: this.assetInfo.total_assets_estimate ? this.assetInfo.total_assets_estimate : null,
                expected_assets_income: this.assetInfo.expected_assets_income ? this.assetInfo.expected_assets_income : null,
                assets_source: this.assetInfo.assets_source && Array.isArray(this.assetInfo.assets_source) ? this.assetInfo.assets_source : [],
            }

            this.accountOwners.forEach((person, personIdx) => {
                let incomeObject = this.getEmptyIncomeSourceLine();
                if (this.assetInfo.incomes && this.assetInfo.incomes[personIdx]){
                    incomeObject = {
                        ...incomeObject,
                        ...this.assetInfo.incomes[personIdx],
                    };
                }

                baseInformation.incomes.push(incomeObject);
            });

            return baseInformation;
        },

        getEmptyIncomeSourceLine() {
            return {
                profession_type: null,
                job_information: null,
                industry: null,
                net_income: null,
            }
        }
    }

});
</script>
